import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';
import { BrtNode, toBunjaRenderTree } from 'react-bunja-devtools/brt';
import { FiberScope } from 'react-bunja-devtools/fiber/provider';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { devtoolsRouteBunja, readRouteParams } from '../../../state';

export const BrtNodeNoContext = createContext<number>(null!);
export const BrtNodeNoScope = createScopeFromContext(BrtNodeNoContext);

export const brtBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const fiber = bunja.use(FiberScope);
  const { routeAtom, changeTab } = bunja.use(devtoolsRouteBunja);

  const collapseDepthAtom = atom(8);

  const reloadCountAtom = atom(0);
  const brtAtom = atom<BrtNode[]>(toBunjaRenderTree(fiber));
  const collapsedNodesAtom = atom<Record<number, boolean>>({});
  const selectedNoAtom = atom((get) => {
    const route = get(routeAtom);
    return readRouteParams(route, 'brt')?.selectedNo ?? 0;
  });

  type NodeTable = Record<number, BrtNode>;
  const nodeTableAtom = atom((get) => {
    const brt = get(brtAtom);
    const table: NodeTable = {};
    function buildTable(nodes: BrtNode[]) {
      nodes.forEach((node) => {
        table[node.no] = node;
        buildTable(node.children);
      });
    }
    buildTable(brt);
    return table;
  });

  function reloadBrt() {
    store.set(reloadCountAtom, (c) => c + 1);
    store.set(brtAtom, toBunjaRenderTree(fiber));
    store.set(collapsedNodesAtom, {});
    changeTab('brt', { selectedNo: 0 });
  }

  function expandAll() {
    store.set(collapsedNodesAtom, {});
  }

  function collapse() {
    const depth = store.get(collapseDepthAtom);
    collapseByDepth(depth);
  }

  function collapseByDepth(maxDepth: number) {
    const brt = store.get(brtAtom);
    const nodesToCollapse: Record<number, boolean> = {};
    function collectNodes(nodes: BrtNode[], currentDepth: number) {
      nodes.forEach((node) => {
        if (currentDepth >= maxDepth) nodesToCollapse[node.no] = true;
        collectNodes(node.children, currentDepth + 1);
      });
    }
    collectNodes(brt, 1);
    store.set(collapsedNodesAtom, nodesToCollapse);
  }

  return {
    reloadCountAtom,
    brtAtom,
    collapseDepthAtom,
    selectedNoAtom,
    nodeTableAtom,
    reloadBrt,
    expandAll,
    collapse,
    collapsedNodesAtom,
  };
});
brtBunja.debugLabel = 'brtBunja';

export const brtNodeBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const no = bunja.use(BrtNodeNoScope);
  const { nodeTableAtom, collapsedNodesAtom, selectedNoAtom } = bunja.use(brtBunja);
  const { changeTab } = bunja.use(devtoolsRouteBunja);

  const nodeAtom = atom((get) => {
    const table = get(nodeTableAtom);
    return table[no];
  });
  const isSelectedAtom = atom((get) => get(selectedNoAtom) === no);
  const isCollapsedAtom = atom((get) => {
    const collapsedNodes = get(collapsedNodesAtom);
    return Boolean(collapsedNodes[no]);
  });

  function select() {
    changeTab('brt', { selectedNo: no });
  }

  function unselect() {
    changeTab('brt', { selectedNo: 0 });
  }

  function toggleIsCollapsed() {
    store.set(collapsedNodesAtom, (prev) => ({ ...prev, [no]: !prev[no] }));
  }

  return {
    nodeAtom,
    isSelectedAtom,
    isCollapsedAtom,
    select,
    unselect,
    toggleIsCollapsed,
  };
});
brtNodeBunja.debugLabel = 'brtNodeBunja';
